import { Button } from "@/components/ui/Button";
import { DialogFooter } from "@/components/ui/Dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/Form";
import { Input } from "@/components/ui/Input";
import { Switch } from "@/components/ui/Switch";
import { useUpdatePaymentChannel } from "@/hooks/services/use-payment-channel";
import {
  type adminPaymentChannelFormValues,
  adminPaymentChannelFormSchema,
} from "@/schemas/payment-channels";
import { AdminPaymentChannel } from "@/services/core/payment-channel.api";
import { useDialogStore } from "@/store/dialog-store";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Minus, Plus } from "lucide-react";
import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";
interface UpdateAdminPaymentChannelFormProps {
  selectedAdminChannel: AdminPaymentChannel | null;
  setSelectedAdminChannel: React.Dispatch<
    React.SetStateAction<AdminPaymentChannel | null>
  >;
  initialData: {
    id: number;
    name: string;
    description: string;
    keyCheck: string[];
    active: boolean;
  };
}

export function UpdateAdminPaymentChannelForm({
  selectedAdminChannel,
  setSelectedAdminChannel,
  initialData,
}: UpdateAdminPaymentChannelFormProps) {
  const updateMutation = useUpdatePaymentChannel();
  const { closeUpdateDialog } = useDialogStore();
  const form = useForm<adminPaymentChannelFormValues>({
    resolver: zodResolver(adminPaymentChannelFormSchema),
    defaultValues: {
      name: initialData.name,
      description: initialData.description,
      keyCheck: initialData.keyCheck || [],
      active: initialData.active,
    },
  });

  // Update form when initialData changes
  useEffect(() => {
    form.reset({
      name: initialData.name,
      description: initialData.description,
      keyCheck: initialData.keyCheck,
      active: initialData.active,
    });
  }, [form, initialData]);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "keyCheck" as never,
  });

  const onSubmit = async (values: adminPaymentChannelFormValues) => {
    if (!selectedAdminChannel) return;
    const data = {
      nonce: "string",
      payload: values,
    };

    try {
      await updateMutation.mutateAsync({ id: initialData.id, data });

      toast.success("Update payment channel successful!");
    } catch (error) {
      console.error("Failed to update payment channel:", error);
    }
    // Close the dialog
    closeUpdateDialog();
    setSelectedAdminChannel(null);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Input placeholder="Enter description" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <FormLabel>Key Check Parameters</FormLabel>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append("")}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Parameter
            </Button>
          </div>

          {fields.length === 0 && (
            <p className="text-muted-foreground text-sm">
              No parameters added. Click 'Add Parameter' to start adding key
              check parameters.
            </p>
          )}

          <div className="space-y-2">
            {fields.map((field, index) => (
              <div key={field.id} className="flex gap-2">
                <FormField
                  control={form.control}
                  name={`keyCheck.${index}`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <Input
                          placeholder={`Parameter ${index + 1}`}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => remove(index)}
                >
                  <Minus className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <FormField
          control={form.control}
          name="active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel>Active Status</FormLabel>
                <FormDescription>
                  Enable or disable this payment channel
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating Channel
              </>
            ) : (
              "Update Channel"
            )}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
